import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { Delete, Key, PersonAdd } from '@mui/icons-material';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';
import { adminFetch, storeAdminSession } from './adminApi';

const PASSWORD_RULES = 'Au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial.';
const PASSWORD_RULES_CAPTION = 'Mot de passe : au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial.';
const NETWORK_ERROR = 'Une erreur est survenue lors de la communication avec le serveur.';
const EMPTY_NEW_ACCOUNT = { matricule: '', password: '', passwordConfirm: '' };
const EMPTY_PASSWORD_CHANGE = { currentPassword: '', newPassword: '', newPasswordConfirm: '' };

const formatDate = (value) => (value ? new Date(value).toLocaleString('fr-FR') : '');

const readError = async (response, fallback) => {
  try {
    const data = await response.json();
    return data.error || data.message || fallback;
  } catch {
    return fallback;
  }
};

const jsonRequest = (method, body) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body)
});

function AdminAccess() {
  const { t } = useAdminLanguage();
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [newAccount, setNewAccount] = useState(EMPTY_NEW_ACCOUNT);
  const [creating, setCreating] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordChange, setPasswordChange] = useState(EMPTY_PASSWORD_CHANGE);
  const [passwordError, setPasswordError] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await adminFetch('/api/access');
      if (!response.ok) {
        setError(await readError(response, 'Erreur lors du chargement des comptes administrateur.'));
        return;
      }
      setAccounts(await response.json());
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleNewAccountChange = (e) => {
    const { name, value } = e.target;
    setNewAccount((prev) => ({
      ...prev,
      [name]: name === 'matricule' ? value.replace(/\D/g, '').slice(0, 9) : value
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!/^\d{9}$/.test(newAccount.matricule)) {
      setError('Le matricule doit comporter exactement 9 chiffres.');
      return;
    }
    if (newAccount.password !== newAccount.passwordConfirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setCreating(true);
    try {
      const response = await adminFetch('/api/access', jsonRequest('POST', newAccount));
      if (!response.ok) {
        setError(await readError(response, 'Erreur lors de la création du compte administrateur.'));
        return;
      }
      setSuccess('Compte administrateur créé avec succès.');
      setNewAccount(EMPTY_NEW_ACCOUNT);
      fetchAccounts();
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setCreating(false);
    }
  };

  const confirmDelete = async () => {
    const account = accountToDelete;
    setAccountToDelete(null);
    resetMessages();
    setDeletingId(account.id);

    try {
      const response = await adminFetch(`/api/access/${account.id}`, { method: 'DELETE' });
      if (!response.ok) {
        setError(await readError(response, 'Erreur lors de la suppression du compte administrateur.'));
        return;
      }
      setSuccess('Compte administrateur supprimé.');
      await fetchAccounts();
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setDeletingId(null);
    }
  };

  const openPasswordDialog = (account) => {
    setPasswordTarget(account);
    setPasswordChange(EMPTY_PASSWORD_CHANGE);
    setPasswordError('');
    setPasswordDialogOpen(true);
  };

  // Le compte ciblé est conservé pour que le contenu ne change pas pendant l'animation de fermeture
  const closePasswordDialog = () => {
    setPasswordDialogOpen(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordChange.newPassword !== passwordChange.newPasswordConfirm) {
      setPasswordError('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }

    const isSelf = passwordTarget.isSelf;
    setSavingPassword(true);
    try {
      const response = isSelf
        ? await adminFetch('/api/access/password', jsonRequest('PUT', passwordChange))
        : await adminFetch(`/api/access/${passwordTarget.id}/password`, jsonRequest('PUT', {
          newPassword: passwordChange.newPassword,
          newPasswordConfirm: passwordChange.newPasswordConfirm
        }));
      if (!response.ok) {
        setPasswordError(await readError(response, 'Erreur lors de la mise à jour du mot de passe.'));
        return;
      }
      const data = await response.json();
      if (isSelf) {
        storeAdminSession(data.token, data.expiresAt);
      }
      closePasswordDialog();
      resetMessages();
      setSuccess(isSelf
        ? 'Mot de passe mis à jour avec succès.'
        : 'Mot de passe du compte administrateur mis à jour.');
      fetchAccounts();
    } catch {
      setPasswordError(NETWORK_ERROR);
    } finally {
      setSavingPassword(false);
    }
  };

  const defaultAccountActive = accounts.some((account) => account.isDefault);
  const selfAccount = accounts.find((account) => account.isSelf);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Comptes administrateur')}
          </Typography>

          <Info type="access" />

          {defaultAccountActive && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("Le compte initial 000000000 est toujours actif. Créez votre propre compte administrateur, connectez-vous avec celui-ci puis supprimez le compte initial.")}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box component="form" onSubmit={handleCreate} autoComplete="off" sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t('Ajouter un compte administrateur')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('Matricule')}
                  name="matricule"
                  value={newAccount.matricule}
                  onChange={handleNewAccountChange}
                  inputProps={{ inputMode: 'numeric', maxLength: 9 }}
                  helperText={t('9 chiffres')}
                  required
                  disabled={creating}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('Mot de passe')}
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  value={newAccount.password}
                  onChange={handleNewAccountChange}
                  required
                  disabled={creating}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('Confirmation')}
                  name="passwordConfirm"
                  type="password"
                  autoComplete="new-password"
                  value={newAccount.passwordConfirm}
                  onChange={handleNewAccountChange}
                  required
                  disabled={creating}
                />
              </Grid>
              <Grid item xs={12} sx={{ pt: '8px !important' }}>
                <Typography variant="caption" color="text.secondary">
                  {t(PASSWORD_RULES_CAPTION)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={creating}
                  startIcon={creating ? <CircularProgress size={20} /> : <PersonAdd />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('Ajouter')}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 1 }}>
            <Typography variant="h6">
              {t('Liste des comptes administrateur')} ({accounts.length} {t('au total')})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Key />}
              onClick={() => openPasswordDialog(selfAccount)}
              disabled={!selfAccount}
              sx={{ textTransform: 'none' }}
            >
              {t('Changer mon mot de passe')}
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('Matricule')}</TableCell>
                  <TableCell>{t('Date de création')}</TableCell>
                  <TableCell>{t('Dernière modification')}</TableCell>
                  <TableCell align="right">{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingAccounts ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {account.matricule}
                          </Typography>
                          {account.isSelf && <Chip label={t('Vous')} color="primary" size="small" />}
                          {account.isDefault && <Chip label={t('Compte initial')} color="warning" size="small" variant="outlined" />}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(account.createdAt)}</TableCell>
                      <TableCell>{formatDate(account.updatedAt)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip
                          title={account.isSelf
                            ? t('Changer mon mot de passe')
                            : t('Modifier le mot de passe')}
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openPasswordDialog(account)}
                          >
                            <Key fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={account.isSelf
                            ? t('Vous ne pouvez pas supprimer votre propre compte')
                            : t('Supprimer')}
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={account.isSelf || deletingId === account.id}
                              onClick={() => setAccountToDelete(account)}
                            >
                              {deletingId === account.id
                                ? <CircularProgress size={18} color="inherit" />
                                : <Delete fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Fade>

      <Dialog open={Boolean(accountToDelete)} onClose={() => setAccountToDelete(null)}>
        <DialogTitle>{t('Confirmer la suppression')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Êtes-vous sûr de vouloir supprimer le compte administrateur')} {accountToDelete?.matricule} ? {t('Cette action est irréversible.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountToDelete(null)} color="primary">
            {t('Annuler')}
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            {t('Supprimer')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passwordDialogOpen} onClose={closePasswordDialog} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={handlePasswordChange} autoComplete="off">
          <DialogTitle>
            {passwordTarget?.isSelf
              ? t('Changer mon mot de passe')
              : `${t('Modifier le mot de passe du compte')} ${passwordTarget?.matricule ?? ''}`}
          </DialogTitle>
          <DialogContent>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t(passwordError)}
              </Alert>
            )}
            {passwordTarget && !passwordTarget.isSelf && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {t('Les sessions ouvertes avec ce compte seront fermées. Communiquez le nouveau mot de passe à son titulaire.')}
              </Alert>
            )}
            {passwordTarget?.isSelf && (
              <TextField
                fullWidth
                margin="dense"
                label={t('Mot de passe actuel')}
                type="password"
                autoComplete="current-password"
                value={passwordChange.currentPassword}
                onChange={(e) => setPasswordChange((prev) => ({ ...prev, currentPassword: e.target.value }))}
                required
                disabled={savingPassword}
              />
            )}
            <TextField
              fullWidth
              margin="dense"
              label={t('Nouveau mot de passe')}
              type="password"
              autoComplete="new-password"
              value={passwordChange.newPassword}
              onChange={(e) => setPasswordChange((prev) => ({ ...prev, newPassword: e.target.value }))}
              helperText={t(PASSWORD_RULES)}
              required
              disabled={savingPassword}
            />
            <TextField
              fullWidth
              margin="dense"
              label={t('Confirmer le nouveau mot de passe')}
              type="password"
              autoComplete="new-password"
              value={passwordChange.newPasswordConfirm}
              onChange={(e) => setPasswordChange((prev) => ({ ...prev, newPasswordConfirm: e.target.value }))}
              required
              disabled={savingPassword}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closePasswordDialog} disabled={savingPassword}>
              {t('Annuler')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={savingPassword}
              startIcon={savingPassword && <CircularProgress size={20} />}
            >
              {savingPassword ? t('Enregistrement...') : t('Enregistrer')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Container>
  );
}

export default AdminAccess;
